import { initialize } from 'launchdarkly-react-client-sdk'
import type { LDClient, LDSingleKindContext } from 'launchdarkly-js-client-sdk'
import { logWarning } from '@/services/errorService'

const clientSideId = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID

const LD_INIT_TIMEOUT_SECONDS = 5

export interface LDLocationData {
	id: string | number
	countryCode?: string
}

export function buildLDContext(location: LDLocationData): LDSingleKindContext {
	return {
		kind: 'location',
		key: String(location.id),
		name: 'SkyTab Online Ordering',
		product: 'STO',
		locationId: Number(location.id),
		countryCode: location.countryCode || 'US',
		language: 'en',
		deploymentEnv: import.meta.env.VITE_APP_ENV || 'development',
	}
}

// Initialised with an anonymous context first; switched to the real venue context
// by initializeLDClient once the location API resolves.
// null when VITE_LAUNCHDARKLY_CLIENT_ID is not set (local dev without LD credentials).
export const ldClient: LDClient | null = clientSideId
	? initialize(clientSideId, { kind: 'user', anonymous: true }, { streaming: true })
	: null

// Holds the last location context so AuthProvider can include it in the
// multi-context identify call on user login, preserving venue-level targeting.
let _currentLocationContext: LDSingleKindContext | null = null

export function getCurrentLocationContext(): LDSingleKindContext | null {
	return _currentLocationContext
}

export async function initializeLDClient(location: LDLocationData): Promise<void> {
	if (!ldClient) return

	try {
		const context = buildLDContext(location)
		await ldClient.identify(context)
		await ldClient.waitForInitialization(LD_INIT_TIMEOUT_SECONDS)
		_currentLocationContext = context
	} catch (error) {
		logWarning('[LaunchDarkly] Failed to identify location context, flags will use defaults', {
			locationId: location.id,
			error,
		})
	}
}

export async function cleanupLDClient(): Promise<void> {
	if (!ldClient) return
	_currentLocationContext = null
	await ldClient.close()
}
